import React from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CommercialLifecycleTimeline } from '@/components/commercial-lifecycle/CommercialLifecycleTimeline';
import { EpcAddendaPanel } from '@/components/epc/commercial-ux/EpcAddendaPanel';
import { EpcClarificationsPanel } from '@/components/epc/commercial-ux/EpcClarificationsPanel';
import { EpcEstimateWorkspaceShell } from '@/components/epc/estimation/EpcEstimateWorkspaceShell';
import { EpcNegotiationPanel } from '@/components/epc/commercial-ux/EpcNegotiationPanel';
import { EpcProposalCockpit } from '@/components/epc/commercial-ux/EpcProposalCockpit';
import { EpcSubmissionCockpit } from '@/components/epc/commercial-ux/EpcSubmissionCockpit';
import { EpcTenderIntakePanel } from '@/components/epc/commercial-ux/EpcTenderIntakePanel';
import { EpcTenderCockpit } from '@/components/epc/commercial-ux/EpcTenderCockpit';
import DocumentList from '@/components/documents/DocumentList';
import DocumentUpload from '@/components/documents/DocumentUpload';
import { pursuitResolvePath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import { epcCommercialService } from '@/services/epcCommercial.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { epcInvitationKind, epcInvitationKindLabel } from '@/constants/epcInvitationKind';
import { mergeInvitationDossier } from '@/constants/epcInvitationWorkflow';
import { EpcTenderInvitationForm } from '@/components/epc/commercial-ux/EpcTenderInvitationForm';
import { EpcInvitationDossierEditor } from '@/components/epc/commercial-ux/EpcInvitationDossierEditor';
import { formatDate } from '@/utils/format';

const { Title, Text, Paragraph } = Typography;

function SimpleTable({
  rows,
  columns,
}: {
  rows: Record<string, unknown>[];
  columns: { title: string; dataIndex: string; width?: number; render?: (v: unknown, r: Record<string, unknown>) => React.ReactNode }[];
}) {
  if (!rows.length) return <Empty description="No records" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <Table
      size="small"
      rowKey={(r) => String(r.id || JSON.stringify(r))}
      dataSource={rows}
      pagination={false}
      columns={columns.map((c) => ({
        ...c,
        render: c.render || ((v: unknown) => (v == null || v === '' ? '—' : String(v))),
      }))}
    />
  );
}

const TENDER_TAB_KEYS = [
  'intake',
  'overview',
  'dossier',
  'lifecycle',
  'documents',
  'scope',
  'estimate',
  'proposals',
  'clarifications',
  'addenda',
  'negotiation',
  'risk',
  'bid-team',
  'submission',
  'approval',
  'history',
] as const;

const EpcTenderWorkspacePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab =
    tabParam && TENDER_TAB_KEYS.includes(tabParam as (typeof TENDER_TAB_KEYS)[number])
      ? tabParam
      : 'intake';
  /** P1 UAT P0: Estimate tab must open immersive Estimating Workbench, not nested classic chrome. */
  const estimateFocus = activeTab === 'estimate';
  const qc = useQueryClient();

  const wsQ = useQuery({
    queryKey: ['epc-tender-workspace', id],
    queryFn: () => epcCommercialService.getTenderWorkspace(id!),
    enabled: Boolean(id),
    staleTime: 15_000,
  });

  const dossierM = useMutation({
    mutationFn: (metaJson: Record<string, unknown>) =>
      epcCommercialService.patchTender(String(id), { metaJson }),
    onSuccess: () => {
      message.success('Dossier saved');
      qc.invalidateQueries({ queryKey: ['epc-tender-workspace', id] });
      qc.invalidateQueries({ queryKey: ['epc-tenders'] });
    },
    onError: (e: Error) => message.error(e.message || 'Save failed'),
  });

  if (!id) return null;

  if (wsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (wsQ.isError || !wsQ.data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={(wsQ.error as Error)?.message || 'Failed to load workspace'} />
      </div>
    );
  }

  const { tender, bidDecisions, proposals, submissions, clarifications, addenda, negotiations, risks, awards } =
    wsQ.data;
  const tenderId = String(tender.id);
  const opportunityId = String(tender.opportunity_id || tender.opportunityId || '') || null;
  const primaryProposal = proposals[0] || null;
  const primarySubmission = submissions[0] || null;

  const items = [
    {
      key: 'intake',
      label: 'Intake',
      children: (
        <EpcTenderIntakePanel
          tender={tender}
          documentCount={submissions.length + addenda.length}
          addendaCount={addenda.length}
        />
      ),
    },
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <>
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Tender #">{String(tender.tender_number || '—')}</Descriptions.Item>
          <Descriptions.Item label="Title">{String(tender.title || '—')}</Descriptions.Item>
          <Descriptions.Item label="Client">{String(tender.client_name || '—')}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag>{String(tender.status || '—')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Commercial model">
            {String(tender.commercial_model || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Currency">{String(tender.currency || '—')}</Descriptions.Item>
          <Descriptions.Item label="Invitation">
            {epcInvitationKindLabel(tender.invitation_kind || tender.invitationKind || 'RFT')}
          </Descriptions.Item>
          <Descriptions.Item label="Invitation ref">
            {String(tender.invitation_ref || '—')}
          </Descriptions.Item>
          <Descriptions.Item label="Bid due">
            {tender.bid_due_at ? formatDate(String(tender.bid_due_at)) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Submission due">
            {tender.submission_due_at ? formatDate(String(tender.submission_due_at)) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Current">
            {tender.is_current === 0 || tender.is_current === false ? 'History' : 'Current'}
          </Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <EpcTenderInvitationForm tender={tender} />
        </div>
        </>
      ),
    },
    {
      key: 'dossier',
      label: 'Dossier',
      children: (
        <EpcInvitationDossierEditor
          kind={epcInvitationKind(tender.invitation_kind || tender.invitationKind) || 'RFT'}
          meta={tender.meta_json || tender.metaJson}
          saving={dossierM.isPending}
          onSave={(dossier) =>
            dossierM.mutate(mergeInvitationDossier(tender.meta_json || tender.metaJson, dossier))
          }
        />
      ),
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      children: <CommercialLifecycleTimeline entityType="epc_tender" entityId={tenderId} />,
    },
    {
      key: 'documents',
      label: 'Documents',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <DocumentUpload entityType="Opportunity" entityId={opportunityId || tenderId} />
          <DocumentList entityType="Opportunity" entityId={opportunityId || tenderId} />
          <Alert
            type="info"
            showIcon
            message="Tender documents are linked via the source opportunity until a dedicated tender entity type is registered."
          />
        </Space>
      ),
    },
    {
      key: 'scope',
      label: 'Scope',
      children: (
        <Paragraph type="secondary">
          Scope / WBS linkage placeholder. Use project WBS when linked.
        </Paragraph>
      ),
    },
    {
      key: 'estimate',
      label: 'Estimate',
      children: null,
    },
    {
      key: 'proposals',
      label: 'Proposal',
      children: (
        <EpcProposalCockpit
          tenderId={tenderId}
          proposal={primaryProposal}
          currency={String(tender.currency || 'AED')}
          onUpdated={() => wsQ.refetch()}
        />
      ),
    },
    {
      key: 'clarifications',
      label: 'Clarifications',
      children: (
        <EpcClarificationsPanel
          tenderId={tenderId}
          rows={clarifications}
          onUpdated={() => wsQ.refetch()}
        />
      ),
    },
    {
      key: 'addenda',
      label: 'Addenda',
      children: (
        <EpcAddendaPanel
          tenderId={tenderId}
          rows={addenda}
          onUpdated={() => wsQ.refetch()}
        />
      ),
    },
    {
      key: 'negotiation',
      label: 'Negotiation',
      children: (
        <EpcNegotiationPanel
          tenderId={tenderId}
          currency={String(tender.currency || 'AED')}
          rows={negotiations}
          onUpdated={() => wsQ.refetch()}
        />
      ),
    },
    {
      key: 'risk',
      label: 'Risk',
      children: (
        <SimpleTable
          rows={risks}
          columns={[
            { title: 'Title', dataIndex: 'title' },
            { title: 'Severity', dataIndex: 'severity', width: 100 },
            { title: 'Status', dataIndex: 'status', width: 100 },
          ]}
        />
      ),
    },
    {
      key: 'bid-team',
      label: 'Bid Team',
      children: (
        <Paragraph type="secondary">Bid team assignments placeholder.</Paragraph>
      ),
    },
    {
      key: 'submission',
      label: 'Submission',
      children: (
        <EpcSubmissionCockpit
          tenderId={tenderId}
          tender={tender}
          submission={primarySubmission}
          proposals={proposals}
          bidDecisions={bidDecisions}
          onUpdated={() => wsQ.refetch()}
        />
      ),
    },
    {
      key: 'approval',
      label: 'Bid decision',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Text type="secondary">Bid decisions linked to this tender</Text>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate('/epc/commercial/bid-decisions')}
            >
              Open bid register
            </Button>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() =>
                navigate(
                  `/epc/commercial/bid-decisions?focus=${encodeURIComponent(
                    bidDecisions[0] ? String(bidDecisions[0].id) : ''
                  )}`
                )
              }
              disabled={!bidDecisions.length}
            >
              Open latest cockpit
            </Button>
          </Space>
          <SimpleTable
            rows={bidDecisions}
            columns={[
              { title: 'Number', dataIndex: 'decision_number', width: 140 },
              {
                title: 'Outcome',
                dataIndex: 'outcome',
                width: 120,
                render: (v) => (v == null || v === '' ? '—' : String(v)),
              },
              { title: 'Status', dataIndex: 'status', width: 120 },
              {
                title: '',
                dataIndex: 'id',
                width: 100,
                render: (v) =>
                  v ? (
                    <Button
                      type="link"
                      style={{ padding: 0 }}
                      onClick={() =>
                        navigate(
                          `/epc/commercial/bid-decisions?focus=${encodeURIComponent(String(v))}`
                        )
                      }
                    >
                      Cockpit
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Space>
      ),
    },
    {
      key: 'history',
      label: 'History',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Awards</Text>
            <SimpleTable
              rows={awards}
              columns={[
                { title: 'Number', dataIndex: 'award_number', width: 140 },
                { title: 'Type', dataIndex: 'award_type', width: 120 },
                { title: 'Status', dataIndex: 'status', width: 120 },
                {
                  title: '',
                  dataIndex: 'id',
                  width: 100,
                  render: (v) =>
                    v ? (
                      <Button
                        type="link"
                        style={{ padding: 0 }}
                        onClick={() =>
                          navigate(
                            `/epc/commercial/awards?focus=${encodeURIComponent(String(v))}`
                          )
                        }
                      >
                        Cockpit
                      </Button>
                    ) : (
                      '—'
                    ),
                },
              ]}
            />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        margin: estimateFocus ? undefined : undefined,
        padding: estimateFocus ? 0 : 24,
        minHeight: estimateFocus ? 'calc(100vh - 64px)' : undefined,
      }}
    >
      {estimateFocus ? (
        <EpcEstimateWorkspaceShell
          focusMode
          immersiveBoq
          tenderId={tenderId}
          tenderTitle={String(tender.title || tender.tender_number || '')}
          bcrNav={{
            onBackTenders: () => navigate('/epc/commercial/tenders'),
            onClassicWorkspace: () => {
              const p = new URLSearchParams(searchParams);
              p.set('tab', 'overview');
              setSearchParams(p, { replace: true });
            },
            onOpenProposal: () =>
              navigate(`/epc/commercial/tenders/${tenderId}/bid?tab=proposal`),
            onOpenPursuit: () => navigate(pursuitResolvePath('epc_tender', tenderId)),
            tenderTitle: String(tender.title || tender.tender_number || ''),
          }}
        />
      ) : (
        <>
          <Space style={{ marginBottom: 12 }} wrap>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/epc/commercial/tenders')}>
              Tenders
            </Button>
            {id ? (
              <Button
                type="primary"
                onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.set('tab', 'estimate');
                  setSearchParams(p, { replace: true });
                }}
              >
                Estimating Workbench
              </Button>
            ) : null}
            {id ? (
              <Button
                onClick={() => {
                  const p = new URLSearchParams(searchParams);
                  p.set('tab', 'estimate');
                  p.set('importBoq', '1');
                  setSearchParams(p, { replace: true });
                }}
              >
                Import BOQ
              </Button>
            ) : null}
            {id ? (
              <Button onClick={() => navigate(`/epc/commercial/tenders/${id}/bid`)}>
                Bid Control Room
              </Button>
            ) : null}
            {id ? (
              <Button onClick={() => navigate(pursuitResolvePath('epc_tender', id))}>
                Open pursuit
              </Button>
            ) : null}
            {id ? (
              <ReportDocumentActions
                reportKey="epc-tender-invitation"
                params={{ id: tenderId }}
                documentId={tenderId}
                size="small"
                allowPreprinted
                sendPermissions={['epc.tender.manage', 'epc.admin']}
              />
            ) : null}
          </Space>
          <Title level={3} style={{ marginBottom: 4 }}>
            <Tag>
              {epcInvitationKind(tender.invitation_kind || tender.invitationKind) === 'RFQ'
                ? 'Request for Quotation'
                : 'Request for Tender'}
            </Tag>{' '}
            {String(tender.title || 'Tender workspace')}{' '}
            <Text type="secondary" style={{ fontSize: 16 }}>
              {String(tender.tender_number || '')}
            </Text>
          </Title>
          <Tag style={{ marginBottom: 16 }}>{String(tender.status || '')}</Tag>

          <EpcTenderCockpit tender={tender} bidDecisions={bidDecisions} />

          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              const p = new URLSearchParams(searchParams);
              if (key === 'intake') p.delete('tab');
              else p.set('tab', key);
              setSearchParams(p, { replace: true });
            }}
            items={items}
          />
        </>
      )}
    </div>
  );
};

export default EpcTenderWorkspacePage;
